import resolveFrontendModule from "@app/resolveModule/frontend";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import type { GesCloudChangePeriodPaymentInfo } from "@ext/enterprise-cloud/GesCloudApi";
import { getAcquiringReturnUrl } from "@ext/enterprise-cloud/utils/getAcquiringReturnUrl";
import t from "@ext/localization/locale/translate";
import { useCallback, useEffect, useState } from "react";
import DateUtils from "../../../../ui-logic/utils/dateUtils";
import {
	ButtonWithLoadingState,
	formatCurrencyWithSymbol,
	getNextPeriodStartDate,
	OfferAcceptanceNotice,
	PayModalLayout,
	PayModalSummary,
	PayModalSummarySeparator,
	PaymentInfoLoader,
	Row,
} from "./paymentModalComponents";
import { usePayModalState } from "./usePayModalState";

export interface ChangePeriodPayModalProps {
	onClose: () => void;
	onSuccess?: () => void;
}

export const ChangePeriodPayModal = ({ onClose, onSuccess }: ChangePeriodPayModalProps) => {
	const { gesCloudApi, open, error, setError, idempotenceKey, onOpenChangeHandler } = usePayModalState(onClose);
	const [paymentInfo, setPaymentInfo] = useState<GesCloudChangePeriodPaymentInfo | null>(null);
	const [paying, setPaying] = useState(false);
	const { isTauri } = usePlatform();

	useEffect(() => {
		const loadPaymentInfo = async () => {
			try {
				const data = await gesCloudApi.getChangePeriodPaymentInfo();
				setPaymentInfo(data);
			} catch {
				setError(t("enterprise-cloud.org-settings.subscription.payment.load-error"));
			}
		};

		void loadPaymentInfo();
	}, [gesCloudApi, setError]);

	const onPay = useCallback(async () => {
		setPaying(true);
		setError(null);
		try {
			const result = await gesCloudApi.changeBillingPeriod(idempotenceKey, {
				returnUrl: getAcquiringReturnUrl(isTauri),
			});

			if (result.status === "redirect") {
				if (isTauri) {
					await resolveFrontendModule("gesCloudOpenAcquiring")(result.redirectUrl);
					onClose();
					return;
				}
				window.location.href = result.redirectUrl;
				return;
			}

			if (result.status === "reserved") onSuccess?.();

			onClose();
		} catch {
			setError(t("enterprise-cloud.org-settings.subscription.payment.pay-error"));
			setPaying(false);
		}
	}, [gesCloudApi, onClose, onSuccess, idempotenceKey, setError, isTauri]);

	const title = t("enterprise-cloud.org-settings.subscription.payment.change-period-title");
	const payNowAmount = paymentInfo ? formatCurrencyWithSymbol(paymentInfo.payNowAmount, paymentInfo.currency) : "";
	const nextPeriodStartDate = getNextPeriodStartDate("year");
	const nextPeriodLabel = t("enterprise-cloud.org-settings.subscription.payment.periodic-payment-from")
		.replace("{periodicPayment}", t("enterprise-cloud.org-settings.subscription.payment.yearly"))
		.replace("{date}", DateUtils.formatDate(nextPeriodStartDate, { dateStyle: "short" }));
	const payButtonLabel = t("enterprise-cloud.org-settings.subscription.payment.pay-amount").replace(
		"{amount}",
		payNowAmount,
	);

	return (
		<PayModalLayout
			description={t("enterprise-cloud.org-settings.subscription.payment.change-period-description")}
			error={error}
			onOpenChange={onOpenChangeHandler}
			open={open}
			title={title}
		>
			{paymentInfo ? (
				<PayModalSummary>
					<div className="flex flex-col gap-2">
						<Row
							accent="primary"
							label={t("enterprise-cloud.org-settings.subscription.payment.due-today-title")}
							value={payNowAmount}
						/>
						<Row
							accent="muted"
							label={t("enterprise-cloud.org-settings.subscription.payment.annual-subscription")}
							value={formatCurrencyWithSymbol(paymentInfo.paymentPerPeriod, paymentInfo.currency)}
						/>
						<Row
							accent="muted"
							label={t("enterprise-cloud.org-settings.subscription.payment.unused-days-credit")}
							value={`-${formatCurrencyWithSymbol(paymentInfo.prorationCredit, paymentInfo.currency)}`}
						/>
					</div>

					<PayModalSummarySeparator />

					<div className="flex flex-col gap-2">
						<Row
							label={nextPeriodLabel}
							value={formatCurrencyWithSymbol(paymentInfo.paymentPerPeriod, paymentInfo.currency)}
						/>
						<Row
							accent="muted"
							label={`${paymentInfo.editorsCount} × ${formatCurrencyWithSymbol(
								paymentInfo.pricePerEditorPerPeriod,
								paymentInfo.currency,
							)}`}
						/>
					</div>
				</PayModalSummary>
			) : (
				<PaymentInfoLoader />
			)}

			<ButtonWithLoadingState
				disabled={!paymentInfo || Boolean(error)}
				isLoading={paying}
				loadingText={title}
				onClick={onPay}
				text={payButtonLabel}
				variant="primary"
			/>
			{paymentInfo && <OfferAcceptanceNotice />}
		</PayModalLayout>
	);
};
